import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { Card, CardBody } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Alert } from '../components/ui/Alert';
import Layout from '../components/layout/Layout';

interface Subscription {
  user_id: number;
  user_email: string;
  user_name: string;
  mx_customer_id: string;
  credits: number;
  created_at: string;
}

interface PaymentLog {
  timestamp: string;
  user_email: string;
  type: string;
  amount: number;
  status: string;
  details: string;
}

const AdminSubscriptions: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [paymentLogs, setPaymentLogs] = useState<PaymentLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'subscriptions' | 'payments'>('subscriptions');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      // Fetch users with MX customer IDs (active subscribers)
      const subsResponse = await axios.get('/api/admin/subscriptions');
      setSubscriptions(subsResponse.data.subscriptions || []);
      
      // Fetch payment logs
      const paymentsResponse = await axios.get('/api/admin/payment-logs');
      setPaymentLogs(paymentsResponse.data.logs || []);
      
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load subscription data');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toFixed(2)}`;
  };

  if (isLoading) {
    return (
      <Layout>
        <div style={{ padding: 'var(--spacing-6)' }}>
          <p>Loading subscription data...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ padding: 'var(--spacing-6)' }}>
        {/* Header */}
        <div style={{ marginBottom: 'var(--spacing-6)' }}>
          <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-bold)', marginBottom: 'var(--spacing-2)' }}>
            Subscription & Payment Management
          </h1>
          <p style={{ color: 'var(--color-gray-600)' }}>
            Track active subscriptions and payment activity
          </p>
        </div>

        {error && (
          <div style={{ marginBottom: 'var(--spacing-4)' }}>
            <Alert variant="error">{error}</Alert>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-6)', borderBottom: '2px solid var(--color-gray-200)' }}>
          <button
            onClick={() => setActiveTab('subscriptions')}
            style={{
              padding: 'var(--spacing-3) var(--spacing-4)',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'subscriptions' ? '2px solid var(--color-primary)' : 'none',
              color: activeTab === 'subscriptions' ? 'var(--color-primary)' : 'var(--color-gray-600)',
              fontWeight: activeTab === 'subscriptions' ? 'var(--font-semibold)' : 'normal',
              cursor: 'pointer',
              marginBottom: '-2px'
            }}
          >
            Active Subscriptions ({subscriptions.length})
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            style={{
              padding: 'var(--spacing-3) var(--spacing-4)',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'payments' ? '2px solid var(--color-primary)' : 'none',
              color: activeTab === 'payments' ? 'var(--color-primary)' : 'var(--color-gray-600)',
              fontWeight: activeTab === 'payments' ? 'var(--font-semibold)' : 'normal',
              cursor: 'pointer',
              marginBottom: '-2px'
            }}
          >
            Payment Logs ({paymentLogs.length})
          </button>
        </div>

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)' }}>
              <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)' }}>
                Active Subscribers
              </h2>
              <Button onClick={fetchData} variant="outline">
                Refresh
              </Button>
            </div>

            {subscriptions.length === 0 ? (
              <Card>
                <CardBody>
                  <p style={{ textAlign: 'center', color: 'var(--color-gray-500)', padding: 'var(--spacing-6)' }}>
                    No active subscriptions found
                  </p>
                </CardBody>
              </Card>
            ) : (
              <div style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
                {subscriptions.map((sub) => (
                  <Card key={sub.user_id}>
                    <CardBody>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 'var(--spacing-4)', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-1)' }}>
                            {sub.user_name}
                          </div>
                          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
                            {sub.user_email}
                          </div>
                        </div>
                        
                        <div>
                          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-1)' }}>
                            Customer ID
                          </div>
                          <div style={{ fontSize: 'var(--text-sm)', fontFamily: 'monospace' }}>
                            {sub.mx_customer_id}
                          </div>
                        </div>
                        
                        <div>
                          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-1)' }}>
                            Credits
                          </div>
                          <div style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', color: 'var(--color-primary)' }}>
                            {sub.credits}
                          </div>
                        </div>
                        
                        <div>
                          <Badge variant="success">Active</Badge>
                        </div>
                      </div>
                      
                      <div style={{ marginTop: 'var(--spacing-3)', paddingTop: 'var(--spacing-3)', borderTop: '1px solid var(--color-gray-200)', fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>
                        Member since: {formatDate(sub.created_at)}
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            )}

            {/* Note about limitations */}
            <div style={{ marginTop: 'var(--spacing-6)', padding: 'var(--spacing-4)', background: 'var(--color-gray-50)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-gray-200)' }}>
              <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-2)' }}>
                ℹ️ Current Limitations
              </h3>
              <ul style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', paddingLeft: 'var(--spacing-5)' }}>
                <li>Subscription details (plan, billing cycle) are tracked in MX Merchant portal</li>
                <li>Contract IDs are logged in server logs - check with: <code style={{ background: 'var(--color-gray-200)', padding: '2px 6px', borderRadius: '4px' }}>sudo journalctl -u mavscope-api | grep "Contract"</code></li>
                <li>To view full subscription details, log into MX Merchant portal</li>
                <li>Future enhancement: Add subscriptions table to database for better tracking</li>
              </ul>
            </div>
          </div>
        )}

        {/* Payment Logs Tab */}
        {activeTab === 'payments' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)' }}>
              <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)' }}>
                Recent Payment Activity
              </h2>
              <Button onClick={fetchData} variant="outline">
                Refresh
              </Button>
            </div>

            {paymentLogs.length === 0 ? (
              <Card>
                <CardBody>
                  <p style={{ textAlign: 'center', color: 'var(--color-gray-500)', padding: 'var(--spacing-6)' }}>
                    No payment logs found
                  </p>
                </CardBody>
              </Card>
            ) : (
              <Card>
                <CardBody>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                          <th style={{ padding: 'var(--spacing-3)', textAlign: 'left', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--color-gray-700)' }}>
                            Timestamp
                          </th>
                          <th style={{ padding: 'var(--spacing-3)', textAlign: 'left', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--color-gray-700)' }}>
                            User
                          </th>
                          <th style={{ padding: 'var(--spacing-3)', textAlign: 'left', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--color-gray-700)' }}>
                            Type
                          </th>
                          <th style={{ padding: 'var(--spacing-3)', textAlign: 'right', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--color-gray-700)' }}>
                            Amount
                          </th>
                          <th style={{ padding: 'var(--spacing-3)', textAlign: 'center', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--color-gray-700)' }}>
                            Status
                          </th>
                          <th style={{ padding: 'var(--spacing-3)', textAlign: 'left', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--color-gray-700)' }}>
                            Details
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentLogs.map((log, index) => (
                          <tr key={index} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                            <td style={{ padding: 'var(--spacing-3)', fontSize: 'var(--text-sm)' }}>
                              {formatDate(log.timestamp)}
                            </td>
                            <td style={{ padding: 'var(--spacing-3)', fontSize: 'var(--text-sm)' }}>
                              {log.user_email}
                            </td>
                            <td style={{ padding: 'var(--spacing-3)', fontSize: 'var(--text-sm)' }}>
                              <Badge variant={log.type === 'subscription' ? 'primary' : 'secondary'}>
                                {log.type}
                              </Badge>
                            </td>
                            <td style={{ padding: 'var(--spacing-3)', fontSize: 'var(--text-sm)', textAlign: 'right', fontWeight: 'var(--font-semibold)' }}>
                              {formatCurrency(log.amount)}
                            </td>
                            <td style={{ padding: 'var(--spacing-3)', textAlign: 'center' }}>
                              <Badge variant={log.status === 'success' ? 'success' : 'error'}>
                                {log.status}
                              </Badge>
                            </td>
                            <td style={{ padding: 'var(--spacing-3)', fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
                              {log.details}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Note about server logs */}
            <div style={{ marginTop: 'var(--spacing-6)', padding: 'var(--spacing-4)', background: 'var(--color-gray-50)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-gray-200)' }}>
              <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-2)' }}>
                📝 Server Logs
              </h3>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-2)' }}>
                For detailed payment logs, check server logs:
              </p>
              <code style={{ display: 'block', background: 'var(--color-gray-900)', color: 'var(--color-gray-100)', padding: 'var(--spacing-3)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-xs)', fontFamily: 'monospace' }}>
                sudo journalctl -u mavscope-api | grep "MX Merchant"
              </code>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminSubscriptions;
